import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TRAVEL_ESIM_INSTALL_STEPS,
  TRAVEL_ESIM_PREREQUISITES,
  TRAVEL_ESIM_SETUP_STEPS,
} from '@/lib/travel-esim-guide';
import { Check } from 'lucide-react';

interface TravelEsimTutorialDialogProps {
  onClose: () => void;
}

/**
 * The install guide of the international eSIM, as the beneficiary receives
 * it by e-mail: prerequisites, when to install, the install steps, and the
 * phone settings once abroad. Opened from the « tuto » link of the catalog.
 */
export function TravelEsimTutorialDialog({ onClose }: TravelEsimTutorialDialogProps) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            Installer l’eSIM internationale
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Le guide que le bénéficiaire reçoit avec son QR code.
          </p>
        </DialogHeader>

        <div className="space-y-5 text-sm text-slate-600">
          <section>
            <h3 className="mb-2 font-semibold text-slate-900">Prérequis</h3>
            <ul className="space-y-1.5">
              {TRAVEL_ESIM_PREREQUISITES.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="mb-2 font-semibold text-slate-900">Quand installer l’eSIM ?</h3>
            <p>
              Dès réception du QR code. Le forfait ne démarre qu’une fois le
              téléphone connecté au réseau du pays de destination.
            </p>
          </section>

          <section>
            <h3 className="mb-2 font-semibold text-slate-900">Installation</h3>
            <div className="space-y-2">
              {TRAVEL_ESIM_INSTALL_STEPS.map((item) => (
                <div key={item.device} className="rounded-lg bg-emerald-50 px-4 py-3">
                  <p className="font-medium text-emerald-800">{item.device}</p>
                  <p className="text-emerald-700">{item.steps}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-1 font-semibold text-slate-900">Paramétrage sur place</h3>
            <p className="mb-2">
              L’eSIM internationale ne gère que les données Internet : les
              appels continuent de passer par la ligne mobile habituelle.
            </p>
            <div className="space-y-2">
              {TRAVEL_ESIM_SETUP_STEPS.map((item) => (
                <div key={item.device} className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="font-medium text-slate-800">{item.device}</p>
                  <ul className="mt-1 space-y-1">
                    {item.steps.map((step) => (
                      <li key={step} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <p className="rounded-lg border border-slate-200 px-4 py-3 text-slate-600">
            Une fois le déplacement terminé, conservez l’eSIM sur le téléphone :
            pour le prochain voyage, il suffira de la recharger avec le forfait
            souhaité.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
